import { useEffect, useState } from "react";
import { Search, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { useElectorates } from "@/components/ElectorateMap";

export const MapSearch = ({ onPick }) => {
  const { data: stats = [] } = useElectorates();
  const [q, setQ] = useState("");
  const [pcResults, setPcResults] = useState(null);
  const [open, setOpen] = useState(false);

  const isPostcode = /^\d{4}$/.test(q.trim());

  useEffect(() => {
    if (!isPostcode) {
      setPcResults(null);
      return;
    }
    let live = true;
    api
      .get(`/postcode/${q.trim()}`)
      .then((r) => live && setPcResults(r.data.electorates))
      .catch(() => live && setPcResults([]));
    return () => {
      live = false;
    };
  }, [q, isPostcode]);

  const nameMatches =
    q.trim().length >= 2 && !isPostcode
      ? stats.filter((s) => s.name.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 8)
      : [];
  const results = isPostcode
    ? (pcResults || []).map((e) => stats.find((s) => s.name === e.name) || { ...e, count: 0 })
    : nameMatches;

  const pick = (name) => {
    onPick(name);
    setOpen(false);
    setQ("");
  };

  return (
    <div className="relative" data-testid="map-search">
      <div className="flex items-center gap-2 bg-[#FAF8F2] border-2 border-[#1B2A41]/15 focus-within:border-[#E2705A] rounded-full px-4 py-2.5 transition-colors">
        <Search className="w-4 h-4 text-[#5C6A79]" />
        <input
          data-testid="map-electorate-search-input"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Search by electorate name or postcode (e.g. Bean or 2906)"
          className="flex-1 bg-transparent outline-none text-sm placeholder:text-[#5C6A79]/70"
        />
      </div>
      {open && q.trim().length >= 2 && (
        <ul
          data-testid="map-search-results"
          className="absolute z-[500] mt-2 w-full bg-[#FAF8F2] border border-[#D8D2C4] rounded-xl shadow-xl overflow-hidden max-h-72 overflow-y-auto"
        >
          {results.length === 0 ? (
            <li className="px-4 py-3 text-sm text-[#5C6A79]">
              {isPostcode && pcResults === null ? "Looking up postcode..." : "No matching electorate"}
            </li>
          ) : (
            results.map((r) => (
              <li key={r.name}>
                <button
                  type="button"
                  data-testid={`search-result-${r.name}`}
                  onMouseDown={() => pick(r.name)}
                  className="w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-[#E5EDE3] text-left"
                >
                  <span className="flex items-center gap-2">
                    <MapPin className="w-3.5 h-3.5 text-[#E2705A]" />
                    <span className="font-semibold text-[#1B2A41]">{r.name}</span>
                    <span className="text-xs text-[#5C6A79]">{r.state}</span>
                  </span>
                  <span className="font-type text-xs text-[#5A7355]">{r.count} F.E.R.M{r.count === 1 ? "" : "s"}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};
